import { apiFetch } from './client';
import { PlanFeatures, PlanLimits } from '../shared/types';
import type { PublicRules } from './admin';
import type { SiteShot } from '../shared/siteContent';

/**
 * The marketing site's data: the public plan catalogue and the community
 * (forum, feature requests, releases). Everything here works anonymously;
 * the write calls need a session and fail with 401 otherwise, which the
 * community pages turn into "sign in to post".
 */

// ---- pricing ----

export interface PublicPlan {
  key: string;
  name: string;
  tagline: string;
  popular: boolean;
  price_monthly_cents: number | null;
  annual_discount_pct: number;
  price_annual_cents: number | null;
  currency: string;
  /** The full row the product enforces — the pricing page renders every
   *  limit and every feature, so the shapes are the shared ones. */
  limits: PlanLimits;
  features: PlanFeatures;
}

/**
 * The catalogue, and beside it the customer-facing rules the service runs on
 * (Platform › Rules): the trial length and the minimum password the pricing
 * and signup pages say. Absent from an older server, whose numbers were the
 * client's own defaults.
 */
export const fetchPublicPlans = () =>
  apiFetch<{ success: true; plans: PublicPlan[]; rules?: PublicRules }>('/site/plans');

// ---- what the site says and shows (Site content, 078) ----

/**
 * The PUBLISHED site document and the screenshot library. `doc` is read
 * through `resolveDoc` (shared/siteContent) before anything draws it.
 */
export const fetchSiteContent = () =>
  apiFetch<{ success: true; content?: { doc?: unknown; shots?: SiteShot[] } }>('/site/content');

// ---- FAQ ----

export interface FaqEntry {
  id: number;
  category: string;
  question: string;
  /** plain text: blank line = paragraph break, [label](url) = link */
  answer: string;
}

export const fetchFaq = () =>
  apiFetch<{ success: true; faq: FaqEntry[] }>('/site/faq');

// ---- forum ----

export type ThreadCategory = 'help' | 'general';

export interface ThreadSummary {
  id: number;
  category: ThreadCategory;
  title: string;
  body: string;
  author_name: string;
  author_is_staff: boolean;
  pinned: boolean;
  locked: boolean;
  created_at: string;
  updated_at: string;
  reply_count: number;
  last_reply_at: string | null;
}

export interface ThreadPost {
  id: number;
  thread_id: number;
  author_name: string;
  author_is_staff: boolean;
  body: string;
  created_at: string;
}

export const fetchThreads = (category?: ThreadCategory) =>
  apiFetch<{ success: true; threads: ThreadSummary[] }>(
    `/community/threads${category ? `?category=${category}` : ''}`
  );

export const fetchThread = (id: number) =>
  apiFetch<{ success: true; thread: ThreadSummary; posts: ThreadPost[]; canModerate?: boolean }>(`/community/threads/${id}`);

// Staff only (the server checks): lock or reopen a thread, remove a thread or one reply.
export const lockThread = (id: number, locked: boolean) =>
  apiFetch<{ success: true }>(`/community/threads/${id}`, { method: 'PATCH', body: JSON.stringify({ locked }) });
export const removeThread = (id: number) =>
  apiFetch<{ success: true }>(`/community/threads/${id}`, { method: 'DELETE' });
export const removeReply = (postId: number) =>
  apiFetch<{ success: true }>(`/community/posts/${postId}`, { method: 'DELETE' });

export const postThread = (input: { category: ThreadCategory; title: string; body: string }) =>
  apiFetch<{ success: true; thread: { id: number } }>('/community/threads', {
    method: 'POST', body: JSON.stringify(input),
  });

export const postReply = (threadId: number, body: string) =>
  apiFetch<{ success: true; post: ThreadPost }>(`/community/threads/${threadId}/posts`, {
    method: 'POST', body: JSON.stringify({ body }),
  });

// ---- feature requests ----

export type RequestStatus = 'open' | 'planned' | 'in_progress' | 'shipped' | 'declined';

export interface FeatureRequest {
  id: number;
  title: string;
  body: string;
  author_name: string;
  author_is_staff: boolean;
  status: RequestStatus;
  staff_note: string;
  created_at: string;
  up_votes: number;
  down_votes: number;
  score: number;
  my_vote: number | null;
}

export const fetchFeatureRequests = () =>
  apiFetch<{ success: true; requests: FeatureRequest[]; canVote: boolean; isStaff: boolean }>(
    '/community/requests'
  );

export const postFeatureRequest = (input: { title: string; body: string }) =>
  apiFetch<{ success: true; id: number }>('/community/requests', {
    method: 'POST', body: JSON.stringify(input),
  });

export const voteFeatureRequest = (id: number, vote: -1 | 0 | 1) =>
  apiFetch<{ success: true }>(`/community/requests/${id}/vote`, {
    method: 'POST', body: JSON.stringify({ vote }),
  });

export const setFeatureRequestStatus = (id: number, status: RequestStatus, staff_note?: string) =>
  apiFetch<{ success: true }>(`/community/requests/${id}`, {
    method: 'PATCH', body: JSON.stringify({ status, staff_note }),
  });

// ---- releases ----

export interface Release {
  id: number;
  version: string;
  title: string;
  released_on: string;
  summary: string;
  highlights: string[];
}

export const fetchReleases = () =>
  apiFetch<{ success: true; releases: Release[] }>('/community/releases');
